import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import { ConfirmChangeReservationModal } from '../../components/ConfirmCancelReservationModal'
import { BOOKING_DETAILS_QUERY } from '../../graphql/bookingDetail'
import { CANCEL_RESERVATION_TA_PANEL_PREVIEW_QUERY } from '../../graphql/cancelReservationTAPanel'
import { ACCOMMODATION_IMAGES_QUERY } from '../../graphql/upload'
import { getTranslate, lang } from '../../localization'
import { BookingDetail, Profile } from '../../types/bookingDetails'
import { CancelRoomStayInfo } from '../../types/cancelReservationTAPanel'
import { Currency } from '../../types/currency'
import { Image } from '../../types/image'
import {
  ModifyReservationInput,
  RoomModifyReserveInput
} from '../../types/modifyReserveTAPanel'
import ReserveDetails from '../BookingDetailsResults/components/ReserveDetails'
import BookingForm from './components/BookingForm'
import RoomStayInformation from './components/RoomStayInformation'
import useStyles from './styles/index.styles'

const BookingDetailsModify = () => {
  const classes = useStyles()
  const params = useParams<{ reserveNumber: string }>()
  const history = useHistory()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const zeroTime = '00:00'
  const [timeLeft, setTimeLeft] = useState('')
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [cancelModalData, setCancelModalData] = useState<{
    penalty: number
    payedPrice: number
    payBack: number
    currency: Currency
  }>({ currency: 'IRR', payBack: 0, payedPrice: 0, penalty: 0 })

  const [modifyReservationInput, setModifyReservationInput] =
    useState<ModifyReservationInput>({
      booker: {
        firstName: '',
        lastName: '',
        fullName: '',
        email: '',
        idNumber: '',
        telephone: '',
        isForeigner: false
      },
      roomStays: []
    })

  const [changeModalIsOpen, setChangeModalIsOpen] = useState(false)
  const [getAccommodationImages, { data: accoommodationImages }] =
    useLazyQuery<{
      accommodationImages: Image[]
    }>(ACCOMMODATION_IMAGES_QUERY, {
      fetchPolicy: 'network-only'
    })

  const { data: bookingDetailData, loading: loadingForBookingDetails } =
    useQuery<{
      bookingDetail: BookingDetail
    }>(BOOKING_DETAILS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        reservationNumber: params.reserveNumber
      },
      onCompleted: (res) => {
        getAccommodationImages({
          variables: {
            propertyCode: res.bookingDetail.basicDetails.propertyCode
          }
        })
        setExpiresAt(res.bookingDetail.basicDetails.expiredAt || null)
        const roomStays: RoomModifyReserveInput[] = res.bookingDetail.rooms.map(
          (room) => {
            const profiles: Profile[] = room.profiles.map((p) => ({
              firstName: p.firstName,
              lastName: p.lastName,
              email: p.email,
              identificationNo: p.identificationNo,
              phone: p.phone,
              isForeigner: p.isForeigner,
              id: p.id
            }))

            return {
              checkIn: room.checkIn,
              checkOut: room.checkOut,
              propertyCode: res.bookingDetail.basicDetails.propertyCode,
              ratePlanCode: room.ratePlanCode,
              displayRatePlanCode: room.displayRatePlanCode,
              displayRoomTypeCode: room.displayRoomTypeCode,
              roomTypeCode: room.roomTypeCode,
              description: '',
              earlyCheckin: false,
              lateCheckout: false,
              modifyReason: '',
              cancelReason: '',
              occupancy: {
                adults: room.occupancy.adults - room.occupancy.extraBeds,
                extraBeds: room.occupancy.extraBeds,
                children: { child1: 0, child2: 0, child3: 0, child4: 0 }
              },
              profiles: profiles,
              useAllocatedInventory: false,
              roomReservationNumber: room.reservationNumber,
              addons: room.addones,
              status: 'Modify',
              currency: res.bookingDetail.basicDetails.currency,
              canCancel: room.canCancel,
              canModify: room.canModify,
              channelID: room.channelID
            }
          }
        )

        setModifyReservationInput({
          booker: {
            firstName: res.bookingDetail.bookerInfo.firstName,
            lastName: res.bookingDetail.bookerInfo.lastName,
            fullName: res.bookingDetail.bookerInfo.fullName,
            email: res.bookingDetail.bookerInfo.email,
            idNumber: res.bookingDetail.bookerInfo.idNumber,
            telephone: res.bookingDetail.bookerInfo.telephone,
            isForeigner: res.bookingDetail.bookerInfo.isForeigner
          },
          roomStays: roomStays
        })
      },
      onError: (error) => {
        toast.error(error.message)
      }
    })

  const [cancelPreview, { loading: loadingForCancelPreview }] = useLazyQuery<{
    cancelReservationTAPanelPreview: CancelRoomStayInfo[]
  }>(CANCEL_RESERVATION_TA_PANEL_PREVIEW_QUERY, {
    onCompleted: (res) => {
      const penalty = res.cancelReservationTAPanelPreview.reduce(
        (sum, item) => sum + item.penalty,
        0
      )
      const payedPrice = res.cancelReservationTAPanelPreview.reduce(
        (sum, item) => sum + item.oldPrice,
        0
      )
      const payBack = payedPrice - penalty
      const currency = res.cancelReservationTAPanelPreview[0].currency
      setCancelModalData({ penalty, payedPrice, payBack, currency })
      setChangeModalIsOpen(true)
    },
    onError: (err) => {
      toast.error(err.message)
      history.push(`/${lang}/booking/list/${params.reserveNumber}/view`)
    }
  })

  const handleApplyForAllRooms = () => {
    setModifyReservationInput((prev) => ({
      ...prev,
      roomStays: prev.roomStays.map((room) => ({
        ...room,
        profiles: room.profiles.map((profile) => ({
          ...profile,
          firstName: prev.booker.firstName,
          lastName: prev.booker.lastName,
          email: prev.booker.email,
          phone: prev.booker.telephone,
          identificationNo: prev.booker.idNumber,
          isForeigner: prev.booker.isForeigner
        }))
      }))
    }))
  }

  useEffect(() => {
    if (!bookingDetailData?.bookingDetail.canConfirm || !expiresAt) return

    const expiry = new Date(expiresAt.replace(' ', 'T'))
    const updateTimer = () => {
      const now = new Date()
      const diff = expiry.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeLeft(zeroTime)
        return
      }

      const minutes = Math.floor(diff / 1000 / 60)
      const seconds = Math.floor((diff / 1000) % 60)
      setTimeLeft(
        `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
          2,
          '0'
        )}`
      )
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [expiresAt])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.herderTitle}>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              className={classes.headerTitle}>
              {getTranslate('Modify Booking')}
            </Typography>
            <Typography variant="h5" className={classes.greyText}>
              {getTranslate('Book a hotel / Modify Booking')}
            </Typography>
          </div>
          <div>
            <Button
              variant="outlined"
              color="primary"
              disabled={
                !bookingDetailData?.bookingDetail.canCancel ||
                loadingForCancelPreview
              }
              onClick={() => {
                cancelPreview({
                  variables: { reservationNumber: params.reserveNumber }
                })
              }}>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                className={classes.redText}>
                {getTranslate('Cancel This Reservation')}
              </Typography>
              {loadingForCancelPreview && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={16}
                />
              )}
            </Button>
            {bookingDetailData?.bookingDetail.canConfirm &&
              bookingDetailData?.bookingDetail.basicDetails.pmsStatus ===
                'Reserved' && (
                <Button
                  variant="contained"
                  className={classes.btn2}
                  disabled={timeLeft === zeroTime}
                  onClick={() => {
                    history.push(
                      `/${lang}/book/${bookingDetailData?.bookingDetail?.sourceInfo.Id}/${bookingDetailData?.bookingDetail?.sourceInfo.channelCode}/${bookingDetailData?.bookingDetail?.sourceInfo.name}/bookstepper/payment/${params.reserveNumber}`
                    )
                  }}>
                  <Typography variant="h4" className={classes.whiteText}>
                    {getTranslate('Confirm Reserve')}
                  </Typography>
                  {timeLeft !== zeroTime && (
                    <Typography variant="h4" className={classes.whiteText}>
                      {timeLeft}
                    </Typography>
                  )}
                </Button>
              )}
            {bookingDetailData?.bookingDetail.basicDetails.pmsStatus ===
              'ModificationReserved' && (
              <Button
                variant="contained"
                className={classes.btn2}
                disabled={timeLeft === zeroTime}
                onClick={() => {
                  const modifiedRoomReservationNumber =
                    bookingDetailData.bookingDetail.rooms.find((room) => {
                      return room.pmsStatus === 'ModificationReserved'
                    })?.reservationNumber
                  history.push(
                    `/${lang}/booking/list/${bookingDetailData?.bookingDetail.basicDetails.reservationNumber}/edit/modify-room-stay/${modifiedRoomReservationNumber}`
                  )
                }}>
                <Typography variant="h4" className={classes.whiteText}>
                  {getTranslate('Confirm Modification')}
                </Typography>
                {timeLeft !== zeroTime && (
                  <Typography variant="h4" className={classes.whiteText}>
                    {timeLeft}
                  </Typography>
                )}
              </Button>
            )}
          </div>
        </div>
        <ReserveDetails
          data={bookingDetailData?.bookingDetail?.basicDetails}
          accommodationImages={accoommodationImages?.accommodationImages}
        />
        <BookingForm
          modifyReservationInput={modifyReservationInput}
          setModifyReservationInput={setModifyReservationInput}
          disabled={!bookingDetailData?.bookingDetail?.canModify!}
        />
        <div className={classes.useBookerInfo}>
          <div className={classes.left}>
            <Typography variant="h4" className={classes.text}>
              {getTranslate('Changes For All Room')}
            </Typography>
            <Typography variant="h5" className={classes.greyText}>
              {getTranslate('Use Booker Info for All Rooms')}
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            disabled={!bookingDetailData?.bookingDetail?.canModify}
            className={classes.button}
            onClick={handleApplyForAllRooms}>
            <Typography variant="h4" className={classes.btnText}>
              {getTranslate('Apply For All Rooms')}
            </Typography>
          </Button>
        </div>
        {modifyReservationInput.roomStays.length > 0 &&
          bookingDetailData?.bookingDetail.rooms.map((room, roomIndex) => (
            <RoomStayInformation
              key={roomIndex}
              roomIndex={roomIndex}
              modifyReservationInput={modifyReservationInput}
              setModifyReservationInput={setModifyReservationInput}
              initialRoom={room}
            />
          ))}
      </div>
      <AdotelSkeleton
        open={loadingForBookingDetails}
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
      <ConfirmChangeReservationModal
        isOpen={changeModalIsOpen}
        mode="cancelation"
        data={cancelModalData}
        onClose={() => setChangeModalIsOpen(false)}
        onClick={() => {
          history.push(
            `/${lang}/booking/list/${params.reserveNumber}/edit/cancel-reserve`
          )
        }}
      />
    </>
  )
}

export default BookingDetailsModify
